import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { avatarModelForPlayer } from '../game/avatarModel';
import { clearSocketBuffer, connectSocket, socket } from '../socket';
import {
  actionButtonClassName,
  playEventSound,
  soundSignatureForEvent
} from '../game/actionFeedback';
import { buildSeatPresentation, normalizeUserId } from '../game/seatPresentation';
import {
  buildRelativeSeatLayout,
  resolveMaxPlayers,
  seatLayoutStyle
} from '../game/tableLayout';
import {
  buildTexasHoldemLobbyRoute,
  buildTexasHoldemSettlementRoute
} from '../games/routes';

export { actionButtonClassName, soundKeyForAction } from '../game/actionFeedback';

export function normalizeRoom(payload) {
  return payload?.room || payload || null;
}

export function normalizeGameState(payload) {
  return payload?.gameState || payload?.state || payload || null;
}

function normalizeNavigationRoom(state) {
  if (!state) {
    return null;
  }
  if (Object.prototype.hasOwnProperty.call(state, 'room')) {
    return state.room || null;
  }
  return state;
}

function normalizeRoomCode(roomCode) {
  return String(roomCode || '').trim().toUpperCase();
}

export function payloadRoomCode(payload) {
  return normalizeRoomCode(
    payload?.roomCode
      || payload?.room?.code
      || payload?.gameState?.roomCode
      || payload?.state?.roomCode
      || payload?.code
  );
}

export function roomScopedErrorMatches(payload, currentRoomCode) {
  const scopedRoomCode = payloadRoomCode(payload);
  return !scopedRoomCode || scopedRoomCode === normalizeRoomCode(currentRoomCode);
}

export function applyRoomScopedSocketError(
  payload = {},
  currentRoomCode,
  { clearBuffer, setError },
  fallbackMessage = '游戏操作失败'
) {
  if (!roomScopedErrorMatches(payload, currentRoomCode)) {
    return false;
  }
  clearBuffer();
  setError(payload.message || fallbackMessage);
  return true;
}

function normalizedSuit(card) {
  if (!card || typeof card === 'string') {
    const suitSymbol = String(card || '').slice(-1);
    return { '♠': 'spades', '♥': 'hearts', '♦': 'diamonds', '♣': 'clubs' }[suitSymbol] || '';
  }
  const suitMap = { s: 'spades', spades: 'spades', h: 'hearts', hearts: 'hearts', d: 'diamonds', diamonds: 'diamonds', c: 'clubs', clubs: 'clubs' };
  return suitMap[String(card.suit || '').toLowerCase()] || '';
}

export function cardColorClass(card) {
  const suit = normalizedSuit(card);
  return suit === 'hearts' || suit === 'diamonds' ? 'card--red' : 'card--black';
}

function formatCard(card) {
  if (!card) {
    return '';
  }
  if (typeof card === 'string') {
    return card;
  }
  const rank = card.rank || card.value || '';
  const suitMap = { s: '♠', spades: '♠', h: '♥', hearts: '♥', d: '♦', diamonds: '♦', c: '♣', clubs: '♣' };
  const suit = suitMap[String(card.suit || '').toLowerCase()] || card.suit || '';
  return `${rank}${suit}`;
}

function eventLabel(event, playersById) {
  if (!event) {
    return '牌桌已更新';
  }
  const name = playersById.get(event.userId) || '玩家';
  if (event.type === 'handStarted') {
    return `第 ${event.handNumber} 手牌开始`;
  }
  if (event.type === 'handSettled') {
    return `第 ${event.handNumber} 手牌结算`;
  }
  if (event.type === 'raise') {
    return `${name} 加注到 ${event.amount}`;
  }
  if (event.type === 'fold') {
    return `${name} 弃牌`;
  }
  if (event.type === 'call') {
    return `${name} 跟注`;
  }
  if (event.type === 'check') {
    return `${name} 过牌`;
  }
  if (event.type === 'allIn') {
    return `${name} all in 到 ${event.amount}`;
  }
  if (event.type === 'showCards') {
    return `${name} 秀牌`;
  }
  return event.text || event.reaction || event.type || '牌桌已更新';
}

function eventKey(event, index) {
  return `${event?.type || 'event'}-${event?.createdAt || ''}-${event?.handNumber || ''}-${event?.userId || ''}-${event?.amount || ''}-${index}`;
}

function actionTypes(actions) {
  return actions.map((action) => (typeof action === 'string' ? action : action.type)).filter(Boolean);
}

export function actionButtonModel(actions = []) {
  const types = actionTypes(actions);
  if (types.includes('fold')) {
    return { label: '弃牌', actionType: 'fold', disabled: false };
  }
  if (types.includes('check')) {
    return { label: '过牌', actionType: 'check', disabled: false };
  }
  return { label: '过牌', actionType: 'check', disabled: true };
}

export function countdownSecondsRemaining(deadlineAt, nowMs = Date.now()) {
  if (!deadlineAt) {
    return null;
  }
  const deadlineMs = Date.parse(deadlineAt);
  if (!Number.isFinite(deadlineMs)) {
    return null;
  }
  return Math.max(0, Math.ceil((deadlineMs - nowMs) / 1000));
}

export function canShowCardsFromActions(actions = []) {
  return actionTypes(actions).includes('showCards');
}

export function showdownRevealControlModel({ gameState, currentPlayer, actions = [] } = {}) {
  if (gameState?.status !== 'showdown' || !currentPlayer) {
    return null;
  }
  if (canShowCardsFromActions(actions)) {
    return { label: '秀牌', actionType: 'showCards', disabled: false };
  }
  const shownCards = gameState?.shownHoleCardsByUserId?.[currentPlayer.userId];
  if (Array.isArray(shownCards) && shownCards.length > 0) {
    return { label: '已秀牌', actionType: null, disabled: true };
  }
  return null;
}

export function roleLabelsForPlayer(player, gameState) {
  if (!player || !gameState) {
    return [];
  }
  const roles = [];
  if (player.userId === gameState.dealerUserId) {
    roles.push({ label: 'D', title: '庄位' });
  }
  if (player.userId === gameState.smallBlindUserId) {
    roles.push({ label: 'SB', title: '小盲' });
  }
  if (player.userId === gameState.bigBlindUserId) {
    roles.push({ label: 'BB', title: '大盲' });
  }
  return roles;
}
export function buildSeatViewModels({
  maxPlayers = resolveMaxPlayers(),
  roomPlayers = [],
  gamePlayers = [],
  pendingJoins = [],
  gameState = {}
} = {}) {
  const gamePlayersBySeat = new Map(gamePlayers.map((player) => [player.seatIndex, player]));
  const pendingBySeat = new Map(pendingJoins.map((player) => [player.seatIndex, player]));
  const roomPlayersBySeat = new Map(roomPlayers.map((player) => [player.seatIndex, player]));
  return Array.from({ length: maxPlayers }, (_, seatIndex) => {
    const gamePlayer = gamePlayersBySeat.get(seatIndex);
    const pendingPlayer = pendingBySeat.get(seatIndex);
    const roomPlayer = roomPlayersBySeat.get(seatIndex);
    const player = gamePlayer || pendingPlayer || roomPlayer || null;
    return {
      seatIndex,
      player,
      empty: !player,
      pending: Boolean(pendingPlayer || roomPlayer?.waitingForNextHand),
      roles: roleLabelsForPlayer(player, gameState)
    };
  });
}

export function isCurrentUserSeatedOrWaiting({
  userId,
  roomPlayers = [],
  gamePlayers = [],
  pendingJoins = []
} = {}) {
  return roomPlayers.some((player) => player.userId === userId)
    || gamePlayers.some((player) => player.userId === userId)
    || pendingJoins.some((player) => player.userId === userId);
}

export function canCurrentUserSitAtSeat({
  seat,
  userId,
  roomStatus,
  roomPlayers = [],
  gamePlayers = [],
  pendingJoins = []
} = {}) {
  return Boolean(userId)
    && (roomStatus === 'playing' || roomStatus === 'lobby')
    && seat?.empty
    && !isCurrentUserSeatedOrWaiting({ userId, roomPlayers, gamePlayers, pendingJoins });
}

export function resultNetLabel(netChips) {
  const amount = Number(netChips) || 0;
  return amount > 0 ? `+${amount}` : String(amount);
}

export function onlinePlayerStatusRows(players = []) {
  return players.map((player) => ({
    userId: player.userId,
    displayName: player.displayName || `玩家 ${player.userId}`,
    status: player.connected === false ? '离线' : '在线'
  }));
}

export function formatTableMessage(event) {
  if (!event) {
    return '';
  }
  return event.type === 'reaction' ? event.reaction || '' : event.text || '';
}

export function latestActionLabel(action) {
  if (!action) {
    return '';
  }
  const amount = Number.isFinite(Number(action.amount)) ? ` ${Number(action.amount)}` : '';
  const labels = {
    check: '过牌',
    fold: '弃牌',
    call: `跟注${amount}`,
    bet: `下注${amount}`,
    raise: `加注${amount}`,
    allIn: `全下${amount}`,
    showCards: '秀牌'
  };
  return labels[action.type] || '';
}

export function buildChatBannerModel(event) {
  if (!event) {
    return null;
  }
  const body = event.type === 'reaction' ? event.reaction : event.text;
  if (!body) {
    return null;
  }
  return {
    id: `${event.createdAt || Date.now()}-${event.userId || ''}-${body}`,
    text: `${event.displayName || '玩家'}：${body}`
  };
}

function compactSeatBadgesForSeat(seat, gameState) {
  const player = seat?.player;
  if (!player) {
    return [];
  }

  const winnerUserIds = Array.isArray(gameState?.winnerUserIds) ? gameState.winnerUserIds : [];
  const isWinner = winnerUserIds.includes(player.userId);
  const isSplitPot = isWinner && winnerUserIds.length > 1;
  const shownCards = gameState?.shownHoleCardsByUserId?.[player.userId];
  const hasShownCards = Array.isArray(shownCards) && shownCards.length > 0;
  const badges = [];

  if (seat?.pending) {
    badges.push('下手入局');
  }
  if (isSplitPot) {
    badges.push('平分');
  } else if (isWinner) {
    badges.push('赢家');
  }
  if (hasShownCards) {
    badges.push('已秀牌');
  }
  if (player.allIn) {
    badges.push('全下');
  }
  if (player.folded) {
    badges.push('已弃牌');
  }

  return badges;
}

export function buildCompactSeatSummary({
  seat,
  currentUserId,
  gameState,
  isActive = false,
  canSit = false,
  roomStatus = '',
  hostUserId = ''
} = {}) {
  const player = seat?.player;
  if (!player) {
    return {
      name: '空位',
      meta: '等待入座',
      value: canSit ? '坐下' : '可入座',
      state: 'empty',
      badges: []
    };
  }

  if (roomStatus === 'lobby') {
    const isSelf = player.userId === currentUserId;
    const name = isSelf ? '你' : player.displayName || `玩家 ${player.userId}`;
    const value = `${player.buyInChips ?? 1000}`;
    const state = '';
    const badges = [];
    if (player.userId === hostUserId) {
      badges.push('房主');
    } else {
      badges.push(player.ready ? '已准备' : '未准备');
    }
    if (player.connected === false) {
      badges.push('离线');
    } else {
      badges.push('在线');
    }
    return {
      name,
      meta: isSelf ? '你' : '已坐下',
      value,
      state,
      badges
    };
  }

  const roles = Array.isArray(seat?.roles) ? seat.roles.map((role) => role.label).join(' ') : '';
  const isSettledStage = gameState?.status === 'showdown' || gameState?.status === 'finished';
  const baseValue = `${player.chips ?? player.buyInChips ?? 0}`;
  let value = baseValue;
  let state = '';
  let badges = seat?.pending ? compactSeatBadgesForSeat(seat, gameState) : [];

  if (seat?.pending) {
    value = '下手';
    state = 'pending';
  } else if (!isSettledStage && player.folded) {
    value = '弃牌';
    state = 'folded';
  } else if (!isSettledStage && player.allIn) {
    value = '全下';
    state = 'all-in';
  } else if (player.committedThisRound > 0) {
    value = `${value} · 下注 ${player.committedThisRound}`;
  }

  if (isSettledStage && !seat?.pending) {
    value = baseValue;
    badges = compactSeatBadgesForSeat(seat, gameState);
    if (player.folded) {
      state = 'folded';
    } else if (player.allIn) {
      state = 'all-in';
    }
  }

  return {
    name: player.userId === currentUserId ? '你' : player.displayName || `玩家 ${player.userId}`,
    meta: [roles || (seat?.pending ? '等待下手' : '游戏中')].filter(Boolean).join(' · '),
    value,
    state,
    badges
  };
}

export function utilityPanelTitle(panel) {
  return {
    events: '最近事件',
    chat: '牌桌聊天',
    settings: '牌桌设置'
  }[panel] || '';
}

function EventsIcon() {
  return <span aria-hidden="true">☰</span>;
}

function ChatIcon() {
  return <span aria-hidden="true">✉</span>;
}

function SettingsIcon() {
  return <span aria-hidden="true">⚙</span>;
}

const GamePage = () => {
  const { roomCode = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [room, setRoom] = useState(() => normalizeNavigationRoom(location.state));
  const [gameState, setGameState] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [raiseAmount, setRaiseAmount] = useState('');
  const [raiseModalOpen, setRaiseModalOpen] = useState(false);
  const [addChipsAmount, setAddChipsAmount] = useState('');
  const [messageText, setMessageText] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeUtilityPanel, setActiveUtilityPanel] = useState(null);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [onlinePlayersOpen, setOnlinePlayersOpen] = useState(false);
  const [confirmEndGame, setConfirmEndGame] = useState(false);
  const [chatBanner, setChatBanner] = useState(null);
  const [error, setError] = useState('');
  const [seatModalOpen, setSeatModalOpen] = useState(false);
  const [targetSeatIndex, setTargetSeatIndex] = useState(-1);
  const [buyInChips, setBuyInChips] = useState('1000');
  const latestSoundSignatureRef = useRef(null);
  const normalizedRoomCode = normalizeRoomCode(roomCode);

  useEffect(() => {
    function payloadMatchesCurrentRoom(payload) {
      return payloadRoomCode(payload) === normalizedRoomCode;
    }

    function handleGameState(payload) {
      if (!payloadMatchesCurrentRoom(payload)) {
        return;
      }
      const nextGameState = normalizeGameState(payload);
      if (nextGameState) {
        setGameState(nextGameState);
        setError('');
      }
    }

    function handleGameEnded(payload = {}) {
      if (!payloadMatchesCurrentRoom(payload)) {
        return;
      }
      navigate(buildTexasHoldemSettlementRoute(normalizedRoomCode), { state: payload });
    }

    function handleRoomUpdated(payload) {
      if (!payloadMatchesCurrentRoom(payload)) {
        return;
      }
      const updatedRoom = normalizeRoom(payload);
      if (updatedRoom) {
        setRoom(updatedRoom);
      }
    }

    function handleError(payload = {}) {
      applyRoomScopedSocketError(payload, normalizedRoomCode, {
        clearBuffer: clearSocketBuffer,
        setError
      });
    }

    function handleMessage(message) {
      setError('');
      setChatBanner(buildChatBannerModel(message));
    }

    socket.on('gameState', handleGameState);
    socket.on('gameEnded', handleGameEnded);
    socket.on('roomUpdated', handleRoomUpdated);
    socket.on('chatMessage', handleMessage);
    socket.on('reaction', handleMessage);
    socket.on('error', handleError);

    connectSocket();
    socket.emit('watchGame', { roomCode: normalizedRoomCode });

    return () => {
      socket.off('gameState', handleGameState);
      socket.off('gameEnded', handleGameEnded);
      socket.off('roomUpdated', handleRoomUpdated);
      socket.off('chatMessage', handleMessage);
      socket.off('reaction', handleMessage);
      socket.off('error', handleError);
    };
  }, [navigate, normalizedRoomCode]);

  useEffect(() => {
    if (!confirmEndGame) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => setConfirmEndGame(false), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [confirmEndGame]);

  useEffect(() => {
    if (!chatBanner) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => setChatBanner(null), 4500);
    return () => window.clearTimeout(timeoutId);
  }, [chatBanner]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  const players = useMemo(
    () => (Array.isArray(gameState?.players) ? gameState.players : []),
    [gameState?.players]
  );
  const roomPlayers = useMemo(
    () => (Array.isArray(room?.players) ? room.players.filter((player) => !player.isBot) : []),
    [room?.players]
  );
  const pendingJoins = useMemo(
    () => (Array.isArray(gameState?.pendingJoins) ? gameState.pendingJoins : []),
    [gameState?.pendingJoins]
  );
  const maxPlayers = resolveMaxPlayers(room?.maxPlayers);

  const tableSeats = useMemo(() => buildSeatViewModels({
    maxPlayers,
    roomPlayers,
    gamePlayers: players,
    pendingJoins,
    gameState
  }), [gameState, maxPlayers, pendingJoins, players, roomPlayers]);

  const currentSeat = tableSeats.find((seat) => seat.player?.userId === user.id) || null;
  const heroSeatIndex = currentSeat?.seatIndex ?? -1;
  const seatLayouts = useMemo(
    () => buildRelativeSeatLayout({ seatCount: maxPlayers, heroSeatIndex, variant: 'game' }),
    [heroSeatIndex, maxPlayers]
  );
  const playersById = useMemo(() => new Map(players.map((player) => [
    player.userId,
    player.displayName || `玩家 ${player.userId}`
  ])), [players]);
  const currentPlayer = players.find((player) => player.userId === user.id);
  const currentUserIsSeatedOrWaiting = isCurrentUserSeatedOrWaiting({
    userId: user.id,
    roomPlayers,
    gamePlayers: players,
    pendingJoins
  });
  const isPendingPlayer = pendingJoins.some((player) => player.userId === user.id)
    || roomPlayers.some((player) => player.userId === user.id && player.waitingForNextHand);
  const isHost = room?.hostUserId === user.id;
  const communityCards = Array.isArray(gameState?.communityCards) ? gameState.communityCards : [];
  const rawActions = Array.isArray(gameState?.legalActions)
    ? gameState.legalActions
    : Array.isArray(gameState?.actions)
      ? gameState.actions
      : Array.isArray(gameState?.availableActions)
        ? gameState.availableActions
        : [];
  const availableActionTypes = actionTypes(rawActions);
  const minRaise = rawActions.find((action) => action?.type === 'raise')?.minAmount || gameState?.minRaise || '';
  const recentEvents = useMemo(() => (Array.isArray(gameState?.recentEvents) ? gameState.recentEvents : []), [gameState?.recentEvents]);
  const recentMessages = Array.isArray(room?.recentMessages) ? room.recentMessages : [];
  const onlineRows = onlinePlayerStatusRows(roomPlayers.length > 0 ? roomPlayers : players);
  const currentResults = Array.isArray(gameState?.currentResults) ? gameState.currentResults : [];
  const primaryAction = actionButtonModel(rawActions);
  const actionCountdown = countdownSecondsRemaining(gameState?.actionDeadlineAt, nowMs);
  const showdownCountdown = countdownSecondsRemaining(gameState?.showdownEndsAt, nowMs);
  const showdownRevealControl = showdownRevealControlModel({
    gameState,
    currentPlayer,
    actions: rawActions
  });
  const showdownUserIds = useMemo(() => (
    Object.entries(gameState?.shownHoleCardsByUserId || {})
      .filter(([, cards]) => Array.isArray(cards) && cards.length > 0)
      .map(([userId]) => normalizeUserId(userId))
      .filter(Boolean)
  ), [gameState?.shownHoleCardsByUserId]);
  const recentActionUserIds = useMemo(() => (
    Object.entries(gameState?.lastActionsByUserId || {})
      .filter(([, action]) => Boolean(action))
      .map(([userId]) => normalizeUserId(userId))
      .filter(Boolean)
  ), [gameState?.lastActionsByUserId]);
  const seatModels = useMemo(() => (
    seatLayouts.map((layoutSeat) => {
      const seat = tableSeats[layoutSeat.seatIndex] || {
        seatIndex: layoutSeat.seatIndex,
        player: null,
        empty: true,
        pending: false,
        roles: []
      };
      const player = seat.player;
      const presentation = buildSeatPresentation({
        seat,
        currentUserId: user.id,
        activeUserId: gameState?.activeUserId,
        roomStatus: room?.status,
        recentActionUserIds,
        showdownUserIds
      });
      const showdownCards = gameState?.shownHoleCardsByUserId?.[player?.userId];
      const revealedCards = presentation.showCards
        ? player?.holeCards
        : (Array.isArray(showdownCards) && showdownCards.length > 0 ? showdownCards : null);

      return {
        ...layoutSeat,
        ...seat,
        player,
        avatar: avatarModelForPlayer(player),
        presentation,
        presentationMode: presentation.mode,
        revealedCards: Array.isArray(revealedCards) ? revealedCards : []
      };
    })
  ), [
    gameState?.activeUserId,
    gameState?.shownHoleCardsByUserId,
    recentActionUserIds,
    room?.status,
    seatLayouts,
    showdownUserIds,
    tableSeats,
    user.id
  ]);

  useEffect(() => {
    const latestEvent = recentEvents.at(-1);
    const nextSignature = soundSignatureForEvent(latestEvent);
    if (latestSoundSignatureRef.current === null) {
      latestSoundSignatureRef.current = nextSignature;
      return;
    }
    if (nextSignature && nextSignature !== latestSoundSignatureRef.current) {
      playEventSound(latestEvent);
    }
    latestSoundSignatureRef.current = nextSignature;
  }, [recentEvents]);

  const sendAction = (type) => {
    setError('');
    socket.emit('gameAction', { roomCode: normalizedRoomCode, action: { type } });
  };

  const handleRaise = (event) => {
    event.preventDefault();
    const amount = Number(raiseAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('请输入有效的加注金额。');
      return;
    }
    const minVal = Number(minRaise) || 1;
    if (amount < minVal) {
      setError(`加注金额不能少于 ${minVal}。`);
      return;
    }
    setError('');
    socket.emit('gameAction', { roomCode: normalizedRoomCode, action: { type: 'raise', amount } });
    setRaiseAmount('');
    setRaiseModalOpen(false);
  };

  const handleAddChips = (event) => {
    event.preventDefault();
    const amount = Number(addChipsAmount);
    if (!Number.isInteger(amount) || amount <= 0) {
      setError('请输入有效的增加筹码数量。');
      return;
    }
    setError('');
    socket.emit('addChips', { roomCode: normalizedRoomCode, amount });
    setAddChipsAmount('');
    setSettingsOpen(false);
  };

  const handleEndGame = () => {
    if (!confirmEndGame) {
      setConfirmEndGame(true);
      return;
    }
    setError('');
    setConfirmEndGame(false);
    setSettingsOpen(false);
    socket.emit('endGame', { roomCode: normalizedRoomCode });
  };

  const handleCancelEndGame = () => {
    setError('');
    setConfirmEndGame(false);
    socket.emit('cancelEndGame', { roomCode: normalizedRoomCode });
  };

  const handleStandUp = () => {
    setError('');
    setSettingsOpen(false);
    socket.emit('standUp', { roomCode: normalizedRoomCode });
  };

  const handleSendMessage = (event) => {
    event.preventDefault();
    const trimmedText = messageText.trim();
    if (!trimmedText) {
      return;
    }
    setError('');
    socket.emit('chatMessage', { roomCode: normalizedRoomCode, text: trimmedText });
    setMessageText('');
  };

  const handleReaction = (reaction) => {
    setError('');
    socket.emit('reaction', { roomCode: normalizedRoomCode, reaction });
  };

  const handleSitDown = (seatIndex) => {
    setError('');
    setTargetSeatIndex(seatIndex);
    setBuyInChips('1000');
    setSeatModalOpen(true);
  };

  const handleConfirmSeatJoin = (event) => {
    event.preventDefault();
    const buyInAmount = Number(buyInChips);
    if (!Number.isInteger(buyInAmount) || buyInAmount <= 0) {
      setError('请输入有效的买入筹码。');
      return;
    }
    setError('');
    setSeatModalOpen(false);
    socket.emit('joinRoom', {
      roomCode: normalizedRoomCode,
      seatIndex: targetSeatIndex,
      buyInChips: buyInAmount
    });
  };

  const handleToggleReady = () => {
    setError('');
    socket.emit('toggleReady', { roomCode: normalizedRoomCode });
  };

  const handleStartGame = () => {
    setError('');
    socket.emit('startGame', { roomCode: normalizedRoomCode });
  };

  const handleUpdateBuyIn = (event) => {
    event.preventDefault();
    const buyInAmount = Number(buyInChips);
    if (!Number.isInteger(buyInAmount) || buyInAmount <= 0) {
      setError('请输入有效的买入筹码。');
      return;
    }
    setError('');
    socket.emit('updateBuyIn', { roomCode: normalizedRoomCode, buyInChips: buyInAmount });
  };

  const renderLobbyActionBar = () => {
    if (room?.status !== 'lobby') {
      return null;
    }

    const seatedPlayer = roomPlayers.find((player) => player.userId === user.id);
    const isHost = room?.hostUserId === user.id;

    if (seatedPlayer) {
      const allGuestsReady = roomPlayers.every((player) => (
        player.userId === room?.hostUserId || player.ready
      ));
      const canStart = isHost && roomPlayers.length >= 2 && allGuestsReady;

      return (
        <section className="floating-action-bar room-actions-bar" style={{ gap: '10px', padding: '12px' }}>
          <div className="action-buttons-group">
            <button
              className={`primary-button ${seatedPlayer.ready ? 'secondary-button' : ''}`}
              type="button"
              onClick={handleToggleReady}
            >
              {seatedPlayer.ready ? '取消准备' : '准备'}
            </button>

            <button
              className="danger-button"
              type="button"
              onClick={handleStandUp}
            >
              站起离座
            </button>

            {isHost && (
              <button
                className="premium-button"
                type="button"
                disabled={!canStart}
                onClick={handleStartGame}
                style={{
                  background: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)',
                  color: '#0f172a',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)'
                }}
              >
                开始游戏
              </button>
            )}
          </div>

          <form className="floating-raise-form" onSubmit={handleUpdateBuyIn} style={{ marginTop: '8px' }}>
            <input
              aria-label="修改筹码"
              inputMode="numeric"
              min="1"
              type="number"
              value={buyInChips}
              onChange={(event) => setBuyInChips(event.target.value)}
              placeholder="修改买入筹码"
            />
            <button className="secondary-button" type="submit">
              修改筹码
            </button>
          </form>
        </section>
      );
    } else {
      return (
        <section className="floating-action-bar spectator-lobby-bar" style={{ textAlign: 'center', padding: '12px' }}>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px' }}>
            您当前是旁观者，点击牌桌上的空闲位置即可坐下加入游戏。
          </p>
        </section>
      );
    }
  };

  const renderFloatingActionBar = () => {
    if (gameState?.status !== 'playing' && gameState?.status !== 'showdown' && gameState?.status !== 'finished') {
      return null;
    }

    return (
      <section className="floating-action-bar">
        {gameState?.endRequestedByUserId && (
          <div className="panel-status panel-status--warning" style={{ textAlign: 'center' }}>
            本手结束后结算
          </div>
        )}

        <div className="game-action-buttons-group">
          <button
            className={actionButtonClassName(primaryAction.actionType)}
            type="button"
            disabled={isPendingPlayer || primaryAction.disabled}
            onClick={() => sendAction(primaryAction.actionType)}
          >
            {primaryAction.label}
          </button>
          <button
            className={actionButtonClassName('call')}
            type="button"
            disabled={isPendingPlayer || !availableActionTypes.includes('call')}
            onClick={() => sendAction('call')}
          >
            跟注
          </button>
          <button
            className={actionButtonClassName('raise')}
            type="button"
            disabled={isPendingPlayer || !availableActionTypes.includes('raise')}
            onClick={() => {
              setRaiseAmount(minRaise ? String(minRaise) : '');
              setError('');
              setRaiseModalOpen(true);
            }}
          >
            加注
          </button>
          <button
            className={actionButtonClassName('allIn')}
            type="button"
            disabled={isPendingPlayer || !availableActionTypes.includes('allIn')}
            onClick={() => sendAction('allIn')}
          >
            all in
          </button>
        </div>
      </section>
    );
  };

  const renderFloatingUtilityGroup = () => (
    <div className="floating-utility-group">
      <button 
        className="floating-utility-button" 
        type="button" 
        aria-label="打开最近事件" 
        onClick={() => setActiveUtilityPanel('events')}
      >
        <EventsIcon />
      </button>
      <button 
        className="floating-utility-button" 
        type="button" 
        aria-label="打开牌桌聊天" 
        onClick={() => setActiveUtilityPanel('chat')}
      >
        <ChatIcon />
      </button>
      <button 
        className="floating-utility-button" 
        type="button" 
        aria-label="打开牌桌设置" 
        onClick={() => setSettingsOpen(true)}
      >
        <SettingsIcon />
      </button>
    </div>
  );

  const renderEventPanelBody = () => (
    <div className="event-list">
      {recentEvents.length === 0 && <p className="muted">还没有手牌事件。</p>}
      {recentEvents.slice(-12).map((event, index) => (
        <div className="event-item" key={eventKey(event, index)}>
          <span>{eventLabel(event, playersById)}</span>
        </div>
      ))}
    </div>
  );

  const renderChatPanelBody = () => (
    <>
      <div className="event-list chat-log">
        {recentMessages.length === 0 && <p className="muted">还没有消息。</p>}
        {recentMessages.slice(-10).map((event, index) => (
          <div className="event-item" key={eventKey(event, index)}>
            <strong>{event.displayName || '玩家'}</strong>
            <span>{formatTableMessage(event)}</span>
          </div>
        ))}
      </div>
      <div className="reaction-row">
        {['Nice', 'Wow', 'Good luck', '😂', '👍', '🔥'].map((reaction) => (
          <button className="secondary-button" type="button" key={reaction} onClick={() => handleReaction(reaction)}>
            {reaction}
          </button>
        ))}
      </div>
      <form className="chat-form" onSubmit={handleSendMessage}>
        <input
          aria-label="聊天消息"
          maxLength="300"
          placeholder="给牌桌发消息"
          value={messageText}
          onChange={(event) => setMessageText(event.target.value)}
        />
        <button type="submit">发送</button>
      </form>
    </>
  );


  return (
    <main className="game-page">
      <div className="game-layout">
        <section className="table-zone">
          {error && <div className="error-message">{error}</div>}

          <div className="table-surface">
            {chatBanner && <div className="chat-flyover" role="status">{chatBanner.text}</div>}
            <div className="table-center-status" style={room?.status === 'lobby' ? { top: '50%' } : {}}>
              {room?.status === 'lobby' && (
                <div className="table-lobby-banner" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  <div style={{ fontSize: '18px', color: '#fbbf24', textShadow: '0 2px 4px rgba(0,0,0,0.5)', fontWeight: 'bold', textAlign: 'center' }}>
                    等待房主开始游戏...
                  </div>
                  <div className="table-room-code-banner" style={{ fontSize: '24px', color: '#ffffff', opacity: 0.9, textShadow: '0 2px 4px rgba(0,0,0,0.5)', fontWeight: '800', letterSpacing: '0.05em', textAlign: 'center' }}>
                    房间号: {roomCode}
                  </div>
                </div>
              )}
              {gameState?.status === 'showdown' && (
                <div className="table-settlement-banner">
                  本手牌已结算{showdownCountdown !== null ? `，${showdownCountdown}秒后下一手` : ''}
                </div>
              )}
              {gameState?.status === 'playing' && actionCountdown !== null && (
                <div className="table-countdown-banner">
                  倒计时 {actionCountdown}秒
                </div>
              )}
            </div>
            {room?.status !== 'lobby' && (
              <div className="table-status">
                <span>底池</span>
                <strong>{gameState?.pot?.total ?? 0}</strong>
              </div>
            )}

            {room?.status !== 'lobby' && (
              <div className="community-row" aria-label="公共牌">
                {communityCards.length === 0 && <span className="table-empty">暂无公共牌</span>}
                {communityCards.map((card, index) => (
                  <span className={`card ${cardColorClass(card)}`} key={`${formatCard(card)}-${index}`}>{formatCard(card)}</span>
                ))}
              </div>
            )}

            <div className="seat-ring">
              {seatModels.length === 0 && <p className="muted">正在等待服务器游戏状态...</p>}
              {seatModels.map((seat) => {
                const player = seat.player;
                const seatIsActive = Boolean(gameState?.activeUserId) &&
                  Boolean(player?.userId) &&
                  normalizeUserId(player?.userId) === normalizeUserId(gameState?.activeUserId);
                const latestAction = gameState?.lastActionsByUserId?.[player?.userId];
                const latestActionText = latestActionLabel(latestAction);
                const canSit = canCurrentUserSitAtSeat({
                  seat,
                  userId: user.id,
                  roomStatus: room?.status,
                  roomPlayers,
                  gamePlayers: players,
                  pendingJoins
                });
                const summary = buildCompactSeatSummary({
                  seat,
                  currentUserId: user.id,
                  gameState,
                  isActive: seatIsActive,
                  canSit,
                  roomStatus: room?.status,
                  hostUserId: room?.hostUserId
                });

                const seatAriaLabel = [
                  summary.name,
                  summary.meta,
                  summary.value,
                  seatIsActive ? '轮到该玩家行动' : '',
                  canSit ? '可点击坐下' : ''
                ].filter(Boolean).join('，');

                const seatActionProps = canSit
                  ? {
                      role: 'button',
                      tabIndex: 0,
                      onClick: () => handleSitDown(seat.seatIndex),
                      onKeyDown: (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleSitDown(seat.seatIndex);
                        }
                      }
                    }
                  : {};

                // Use the new seat-v2 structure
                return (
                  <article
                    aria-label={seatAriaLabel}
                    className={`seat-v2 seat-v2--slot-${seat.slot || 'none'} seat-v2--size-${seat.size || 'none'} ${seatIsActive ? 'seat-v2--active' : ''} ${seat.empty ? 'seat-v2--empty' : ''}`}
                    key={seat.seatIndex}
                    style={seatLayoutStyle(seat)}
                    {...seatActionProps}
                  >
                    {gameState?.status === 'playing' && seatIsActive && (
                      <div className="seat-v2__thinking-badge">思考中</div>
                    )}
                    <div className="seat-v2__avatar">
                      {seat.avatar.src ? (
                        <img
                          alt={seat.avatar.alt}
                          src={seat.avatar.src}
                        />
                      ) : (
                        <span className="seat-avatar--fallback">
                          {seat.avatar.fallbackLabel}
                        </span>
                      )}
                    </div>

                    <div className="seat-v2__info-pill">
                      <div className="seat-v2__name">{summary.name}</div>
                      <div className="seat-v2__chips">{summary.value}</div>
                    </div>

                    {seat.roles.some(r => r.label === 'D') && (
                      <div className="seat-v2__dealer">D</div>
                    )}

                    {player?.userId === user.id && showdownRevealControl && (
                      <button
                        className="show-cards-btn"
                        type="button"
                        disabled={showdownRevealControl.disabled}
                        onClick={showdownRevealControl.actionType ? () => sendAction(showdownRevealControl.actionType) : undefined}
                      >
                        {showdownRevealControl.label}
                      </button>
                    )}

                    <div className="seat-v2__cards">
                      {seat.revealedCards.length > 0
                        ? seat.revealedCards.map((card, idx) => (
                          <span className={`card card--small ${cardColorClass(card)}`} key={`${formatCard(card)}-${idx}`}>{formatCard(card)}</span>
                        ))
                        : (player?.cardCount > 0 ? Array.from({ length: player.cardCount }).map((_, idx) => (
                          <span className="card card--hidden card--small" key={idx}>?</span>
                        )) : null)}
                    </div>

                    {/* Latest action floating near seat */}
                    {latestActionText && (
                      <div className="seat-action-strip" style={{ position: 'absolute', top: '40px', right: '-40px', zIndex: 20 }}>
                        {latestActionText}
                      </div>
                    )}

                    {/* Status badges */}
                    {summary.badges.length > 0 && (
                      <div className="seat-flags" style={{ marginTop: '2px' }}>
                        {summary.badges.map((badge) => (
                          <span key={badge} style={{ fontSize: '9px', padding: '2px 6px' }}>{badge}</span>
                        ))}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      </div>

      {renderFloatingActionBar()}
      {renderLobbyActionBar()}
      {renderFloatingUtilityGroup()}

      {activeUtilityPanel && (
        <div className="modal-backdrop mobile-utility-modal" role="presentation">
          <section className="modal-card mobile-utility-card" role="dialog" aria-modal="true" aria-label={utilityPanelTitle(activeUtilityPanel)}>
            <div className="section-heading">
              <h2>{utilityPanelTitle(activeUtilityPanel)}</h2>
              <button className="secondary-button compact-button" type="button" onClick={() => setActiveUtilityPanel(null)}>
                关闭
              </button>
            </div>
            {activeUtilityPanel === 'events' && renderEventPanelBody()}
            {activeUtilityPanel === 'chat' && renderChatPanelBody()}
          </section>
        </div>
      )}
      {settingsOpen && (
        <div className="modal-backdrop table-settings-modal" role="presentation">
          <section className="modal-card settings-card" role="dialog" aria-modal="true" aria-label="牌桌设置">
            <div className="section-heading">
              <div>
                <p className="eyebrow">牌桌设置</p>
                <h2>牌桌设置</h2>
                <p className="settings-meta">房间 {roomCode} · 第 {gameState?.handNumber || '-'} 手牌</p>
              </div>
              <button className="secondary-button compact-button" type="button" onClick={() => setSettingsOpen(false)}>
                关闭
              </button>
            </div>
            <form className="chip-add-form" onSubmit={handleAddChips}>
              <label className="field-label">
                增加筹码
                <input
                  aria-label="增加筹码"
                  inputMode="numeric"
                  min="1"
                  placeholder="下一手生效"
                  type="number"
                  value={addChipsAmount}
                  onChange={(event) => setAddChipsAmount(event.target.value)}
                />
              </label>
              <button type="submit">增加筹码</button>
            </form>
            <div className="settings-results">
              <Link className="secondary-button link-button" to="/">
                返回游戏库
              </Link>
            </div>
            <div className="settings-results">
              <Link className="secondary-button link-button" to={buildTexasHoldemLobbyRoute()}>
                返回德州大厅
              </Link>
            </div>
            <div className="settings-results">
              <button className="secondary-button compact-button" type="button" onClick={() => setResultsOpen((open) => !open)}>
                当前战绩
              </button>
              {resultsOpen && (
                <div className="current-results-panel">
                  {currentResults.length === 0 && <p className="muted">暂无战绩。</p>}
                  {currentResults.map((result, index) => (
                    <div className="current-result-row" key={result.userId}>
                      <span>{index + 1}</span>
                      <strong>{result.displayName || `玩家 ${result.userId}`}</strong>
                      <span>{result.chips} / {result.totalBuyInChips}</span>
                      <b className={result.netChips >= 0 ? 'result-net result-net--positive' : 'result-net result-net--negative'}>
                        {resultNetLabel(result.netChips)}
                      </b>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="settings-results">
              <button className="secondary-button compact-button" type="button" onClick={() => setOnlinePlayersOpen((open) => !open)}>
                玩家在线
              </button>
              {onlinePlayersOpen && (
                <div className="compact-player-list settings-online-list">
                  {onlineRows.length === 0 && <p className="muted">暂无玩家信息。</p>}
                  {onlineRows.map((player) => (
                    <div key={player.userId}>
                      <strong>{player.displayName}</strong>
                      <span>{player.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {currentUserIsSeatedOrWaiting && (
              <div className="settings-danger-zone">
                <div>
                  <strong>站起离座</strong>
                  <p className="muted">当前手牌会按弃牌处理，座位会立即释放。</p>
                </div>
                <button className="danger-button danger-button--compact" type="button" onClick={handleStandUp}>
                  站起离座
                </button>
              </div>
            )}
            {isHost && gameState?.endRequestedByUserId ? (
              <div className="settings-danger-zone">
                <div>
                  <strong>本手结束后结算</strong>
                  <p className="muted">牌局将在当前手牌完成后进入结算页。</p>
                </div>
                <button className="secondary-button compact-button" type="button" onClick={handleCancelEndGame}>
                  取消结束申请
                </button>
              </div>
            ) : isHost ? (
              <div className="settings-danger-zone">
                <div>
                  <strong>结束比赛</strong>
                  <p className="muted">当前手牌完成后，所有玩家会进入结算页。</p>
                </div>
                <button className="danger-button danger-button--compact" type="button" onClick={handleEndGame}>
                  {confirmEndGame ? '确认本手后结算' : '本手结束后结算'}
                </button>
              </div>
            ) : null}
          </section>
        </div>
      )}
      {seatModalOpen && (
        <div className="modal-backdrop seat-join-modal" role="presentation">
          <section className="modal-card settings-card" role="dialog" aria-modal="true" aria-label="选择筹码入座">
            <div className="section-heading">
              <div>
                <p className="eyebrow">选择筹码</p>
                <h2>入座座位 #{targetSeatIndex + 1}</h2>
              </div>
              <button className="secondary-button compact-button" type="button" onClick={() => setSeatModalOpen(false)}>
                取消
              </button>
            </div>
            <form className="chip-add-form" onSubmit={handleConfirmSeatJoin}>
              <label className="field-label">
                买入筹码金额
                <input
                  aria-label="买入筹码数量"
                  inputMode="numeric"
                  min="1"
                  type="number"
                  value={buyInChips}
                  onChange={(event) => setBuyInChips(event.target.value)}
                  autoFocus
                />
              </label>
              <button type="submit">确认入座</button>
            </form>
          </section>
        </div>
      )}
      {raiseModalOpen && (
        <div className="modal-backdrop raise-action-modal" role="presentation">
          <section className="modal-card settings-card" role="dialog" aria-modal="true" aria-label="加注金额">
            <div className="section-heading">
              <div>
                <p className="eyebrow">行动选项</p>
                <h2>加注金额</h2>
                {minRaise && <p className="settings-meta">最低加注金额: {minRaise}</p>}
              </div>
              <button className="secondary-button compact-button" type="button" onClick={() => setRaiseModalOpen(false)}>
                取消
              </button>
            </div>
            <form className="chip-add-form" onSubmit={handleRaise}>
              <label className="field-label">
                加注金额
                <input
                  aria-label="加注金额"
                  inputMode="numeric"
                  min="1"
                  placeholder={minRaise ? `加注 (至少 ${minRaise})` : '输入加注金额'}
                  type="number"
                  value={raiseAmount}
                  onChange={(event) => setRaiseAmount(event.target.value)}
                  autoFocus
                />
              </label>
              <button className={actionButtonClassName('raise')} type="submit">
                确认加注
              </button>
            </form>
          </section>
        </div>
      )}
    </main>
  );
};

export default GamePage;
